import { useState } from 'react'
import { AlmanacFile, AlmanacFileSummary, BaseEmoji } from 'types'
import { ProjectCard } from 'components/BlazeCommandLine/components/ProjectCard'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateDoc } from 'components/NewEntrypointModal/hooks/useCreateDoc'
import { useCloseLeftSidebar } from 'hooks/useCloseLeftSidebar'
import { generatePath, useHistory } from 'react-router-dom'
import { DESIGN_PATH, DOC_PATH } from 'paths'
import { useCreateDesign } from 'hooks/queries/workspaces/useCreateDesign'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from './NewFileDrawer.module.scss'
import { Document, Design } from 'blaze-ui/icons'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Drawer } from 'components/Drawer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ASPECT_RATIOS, AspectRatioKey, DesignChannels } from 'components/NewFileModal/Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { DrawerProps } from 'components/Drawer/Root'
import { FileLocationPicker } from './FileLocationPicker'
import { SelectDesignFormatDrawer } from './SelectAspectRatioDrawer/SelectDesignFormatDrawer'
import { Button as BlazeButton } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'

type FileType = 'document' | 'design'

interface Props extends DrawerProps {
  project?: AlmanacFile | null
  onSuccess?: () => void
  initialFileType?: FileType
  initialAspectRatioKey?: AspectRatioKey
}

export const NewFileDrawer = observer(
  ({
    project,
    onSuccess,
    initialFileType = 'document',
    initialAspectRatioKey = 'instagram-square-post',
    ...props
  }: Props) => {
    const { drawersStore } = useRootStore()
    const workspaceId = useCurrentWorkspaceId()
    const [title, setTitle] = useState<string>('')
    const [fileType, setFileType] = useState<FileType>(initialFileType)
    const [emoji, setEmoji] = useState<BaseEmoji | null>(null)
    const { closeLeftSidebar } = useCloseLeftSidebar()
    const history = useHistory()
    const [chosenFolder, setChosenFolder] = useState(project)
    const [designFormat, setDesignFormat] = useState<AspectRatioKey>(initialAspectRatioKey)
    const aspectRatio = ASPECT_RATIOS[designFormat]
    const designerEnabled = useFlipperFlag('visual_studio')
    const close = () => drawersStore.closeDrawer()

    const { createDoc, isLoading } = useCreateDoc({
      chosenWorkspaceId: workspaceId,
      chosenFolder: { project_id: chosenFolder?.project_id, handbook: chosenFolder?.handbook },
      docOrFolderName: title,
      emoji: emoji?.native,
      onSuccess: async (data: { almanac_file: AlmanacFile | Omit<AlmanacFileSummary, 'ancestors'> }) => {
        closeLeftSidebar()
        close()
        onSuccess?.()
        history.push(generatePath(DOC_PATH, { slug: data.almanac_file.id }))
      },
    })

    const designGenerationMutation = useCreateDesign(
      { workspaceId: workspaceId },
      {
        onSuccess: (res: any) => {
          close()
          onSuccess?.()
          history.push(generatePath(DESIGN_PATH, { key: res.almanac_file.id }))
        },
        onError: () => {
          showToastMessage('Something went wrong.', 'error')
        },
      },
    )

    const createDesign = async () => {
      const { width, height } = ASPECT_RATIOS[designFormat]
      designGenerationMutation.mutate({
        almanac_file: {
          title,
          parent_id: chosenFolder?.id,
          header_emoji: emoji?.native,
        },
        width,
        height,
        channel: DesignChannels[designFormat],
      })
    }

    const handleCreate = async () => {
      if (fileType === 'document') {
        createDoc()
      } else {
        createDesign()
      }
    }

    const isCreating = isLoading || designGenerationMutation.isLoading

    return (
      <Drawer.NestedRoot {...props}>
        <Drawer.Portal>
          <div className={Styles.overlayNested}>
            <Drawer.Overlay />
          </div>
          <Drawer.Content fullHeight>
            <Drawer.InnerContent isSnappedTop>
              <Drawer.Top>
                <Drawer.Back variant="ghost" onPress={close} />
                <Drawer.Title asChild>
                  <Heading level={5}>New File</Heading>
                </Drawer.Title>
                <Drawer.Action
                  variant="ghost"
                  onPress={handleCreate}
                  isDisabled={isCreating || !title}
                  position="right"
                >
                  Create
                </Drawer.Action>
              </Drawer.Top>
              <Drawer.Container>
                <FileLocationPicker
                  chosenFolder={chosenFolder}
                  setChosenFolder={setChosenFolder}
                  setTitle={setTitle}
                  title={title}
                  emoji={emoji}
                  setEmoji={setEmoji}
                />

                <div className={Styles.section}>
                  {fileType === 'design' && (
                    <>
                      <div className={Styles.label}>
                        <Text variant="smallList">Format</Text>
                      </div>
                      <BlazeButton
                        onPress={() =>
                          drawersStore.openDrawer(SelectDesignFormatDrawer, {
                            selected: designFormat,
                            onSelect: setDesignFormat,
                          })
                        }
                        fullWidth
                        variant="secondary"
                        size="lg"
                        withChevron="down"
                      >
                        {`${aspectRatio.name} (${aspectRatio.width}x${aspectRatio.height})`}
                      </BlazeButton>
                    </>
                  )}
                </div>

                {designerEnabled && (
                  <div className={Styles.section}>
                    <div className={Styles.grid}>
                      <ProjectCard
                        title="Document"
                        description="Blog post, brainstorm, copy ideas, email etc. "
                        icon={Document}
                        color="var(--teal-50)"
                        isSelected={fileType === 'document'}
                        onClick={() => setFileType('document')}
                        dataTestId="document-card"
                      />
                      <ProjectCard
                        title="Design"
                        description="Social media post, landing page, etc."
                        icon={Design}
                        color="var(--green-50)"
                        isSelected={fileType === 'design'}
                        onClick={() => setFileType('design')}
                        dataTestId="design-card"
                      />
                    </div>
                  </div>
                )}
              </Drawer.Container>
            </Drawer.InnerContent>
          </Drawer.Content>
          <Drawer.Overlay />
        </Drawer.Portal>
      </Drawer.NestedRoot>
    )
  },
)
